import type { BookChapter, BookSection } from '@/components/books/book';
import type { SectionPlacement } from '@/lib/add-ma-section';

export type DragKind = 'section' | 'chapter' | null;

export function renumberChapterTree(chapters: BookChapter[]): BookChapter[] {
    let regularChapterIndex = 0;

    return chapters.map(chapter => {
        if (chapter.number === 0) {
            return {
                ...chapter,
                id: `ch-0`,
                number: 0,
                sections: chapter.sections.map((section, sectionIndex) => ({
                    ...section,
                    number: `0.${sectionIndex + 1}`,
                })),
            };
        }

        regularChapterIndex += 1;
        const chapterNumber = regularChapterIndex;
        return {
            ...chapter,
            id: `ch-${chapterNumber}`,
            number: chapterNumber,
            sections: chapter.sections.map((section, sectionIndex) => ({
                ...section,
                number: `${chapterNumber}.${sectionIndex + 1}`,
            })),
        };
    });
}

function removeEmptyChapters(chapters: BookChapter[]): BookChapter[] {
    return chapters.filter(chapter => chapter.sections.length > 0);
}

export function sectionMoveDeletesSourceChapter(chapters: BookChapter[], activeSectionId: string, targetChapterId: string): boolean {
    const sourceChapter = findChapterContainingSection(chapters, activeSectionId);
    if (!sourceChapter || sourceChapter.number === 0 || sourceChapter.sections.length !== 1) {
        return false;
    }

    const targetChapter = chapters.find(chapter => chapter.id === targetChapterId);
    if (!targetChapter || targetChapter.number === 0 || sourceChapter.id === targetChapter.id) {
        return false;
    }

    return true;
}

export function applySectionDrop(
    chapters: BookChapter[],
    activeSectionId: string,
    targetChapterId: string,
    overIndex: number,
    options?: { keepEmptyChapters?: boolean },
): BookChapter[] {
    const sourceChapter = findChapterContainingSection(chapters, activeSectionId);
    if (!sourceChapter || sourceChapter.number === 0) {
        return chapters;
    }

    const sectionIndex = sourceChapter.sections.findIndex(section => section.id === activeSectionId);
    if (sectionIndex === -1) {
        return chapters;
    }

    const targetChapter = chapters.find(chapter => chapter.id === targetChapterId);
    if (!targetChapter || targetChapter.number === 0) {
        return chapters;
    }

    const section = sourceChapter.sections[sectionIndex]!;
    const adoptedFk = targetChapter.ma_zybook_chapter_id;
    const sectionToInsert: BookSection =
        sourceChapter.id === targetChapterId
            ? section
            : {
                  ...section,
                  ma_zybook_chapter_id: adoptedFk,
              };

    const withoutSection = chapters.map(chapter => {
        if (chapter.id !== sourceChapter.id) {
            return chapter;
        }

        const sections = [...chapter.sections];
        sections.splice(sectionIndex, 1);
        return { ...chapter, sections };
    });

    const withInsert = withoutSection.map(chapter => {
        if (chapter.id !== targetChapterId) {
            return chapter;
        }

        const sections = [...chapter.sections];
        const insertIndex = Math.max(0, Math.min(overIndex, sections.length));
        sections.splice(insertIndex, 0, sectionToInsert);
        return { ...chapter, sections };
    });

    const moved = options?.keepEmptyChapters ? withInsert : removeEmptyChapters(withInsert);
    return renumberChapterTree(moved);
}

export function findChapterContainingSection(chapters: BookChapter[], sectionId: string): BookChapter | undefined {
    return chapters.find(chapter => chapter.sections.some(section => section.id === sectionId));
}

export function resolveChapterIdAfterRenumber(chapters: BookChapter[], dragStartChapterId: string, anchorSectionId: string | null): string {
    if (anchorSectionId) {
        const chapter = findChapterContainingSection(chapters, anchorSectionId);
        if (chapter) {
            return chapter.id;
        }
    }

    return chapters.find(chapter => chapter.id === dragStartChapterId)?.id ?? dragStartChapterId;
}

export type ChapterDropTargetType = 'chapter' | 'section';

export function resolveChapterOverIndex(
    chapters: BookChapter[],
    targetChapterId: string,
    targetType: ChapterDropTargetType,
    activeChapterId?: string,
): number {
    const chapterIndex = chapters.findIndex(chapter => chapter.id === targetChapterId);
    if (chapterIndex === -1) {
        return 0;
    }

    if (targetType === 'chapter') {
        if (activeChapterId) {
            const activeIndex = chapters.findIndex(chapter => chapter.id === activeChapterId);
            if (activeIndex !== -1 && activeIndex === chapterIndex + 1) {
                return chapterIndex;
            }
        }

        return chapterIndex + 1;
    }

    return chapterIndex;
}

export function applyChapterDrop(
    chapters: BookChapter[],
    activeChapterId: string,
    overIndex: number,
    options?: { renumberIds?: boolean },
): BookChapter[] {
    const activeChapter = chapters.find(chapter => chapter.id === activeChapterId);
    if (!activeChapter || activeChapter.number === 0) {
        return chapters;
    }

    const aboutIndex = chapters.findIndex(chapter => chapter.number === 0);
    if (aboutIndex !== -1 && overIndex <= aboutIndex) {
        return chapters;
    }

    const activeIndex = chapters.findIndex(chapter => chapter.id === activeChapterId);
    if (activeIndex === -1) {
        return chapters;
    }

    const reordered = [...chapters];
    const [movedChapter] = reordered.splice(activeIndex, 1);
    const insertIndex = Math.max(0, Math.min(overIndex, reordered.length));
    reordered.splice(insertIndex, 0, movedChapter!);

    if (options?.renumberIds === false) {
        return reordered;
    }

    return renumberChapterTree(reordered);
}

export function mergeSectionAfterAdd(
    chapters: BookChapter[],
    placement: SectionPlacement,
    section: {
        id: string;
        title: string;
        shortTitle?: string;
        testQuestionDocumentId?: number | null;
        testQuestionDocumentLastEditedAt?: string | null;
        maZybookChapterId?: number;
    },
): BookChapter[] {
    const existingChapter = chapters.find(chapter => chapter.number === placement.chapterNumber);
    const chapterFk = section.maZybookChapterId ?? existingChapter?.ma_zybook_chapter_id;
    if (chapterFk == null) {
        return chapters;
    }
    const sectionShortTitle = section.shortTitle?.trim() || section.title;
    const chapterShortTitle = placement.chapterShortTitle?.trim() || placement.chapterTitle;

    const newSection: BookSection = {
        id: section.id,
        number: `${placement.chapterNumber}.${placement.sectionNumber}`,
        title: section.title,
        shortTitle: sectionShortTitle,
        ma_zybook_chapter_id: chapterFk,
        testQuestionDocumentId: section.testQuestionDocumentId ?? null,
        testQuestionDocumentLastEditedAt: section.testQuestionDocumentLastEditedAt ?? null,
        placement: 'included',
        testBankPlacement: 'included',
    };

    if (existingChapter) {
        return chapters.map(chapter => {
            if (chapter.number !== placement.chapterNumber) {
                return chapter;
            }

            return {
                ...chapter,
                title: placement.chapterTitle,
                shortTitle: chapter.shortTitle || chapterShortTitle,
                sections: [...chapter.sections, newSection],
            };
        });
    }

    return [
        ...chapters,
        {
            id: `ch-${placement.chapterNumber}`,
            number: placement.chapterNumber,
            title: placement.chapterTitle,
            shortTitle: chapterShortTitle,
            ma_zybook_chapter_id: chapterFk,
            sections: [newSection],
        },
    ];
}

function sectionSuffix(number: string): number | null {
    const suffix = Number(number.split('.')[1]);
    return Number.isInteger(suffix) ? suffix : null;
}

function withNumber(section: BookSection, chapterNumber: number, sectionNumber: number): BookSection {
    return { ...section, number: `${chapterNumber}.${sectionNumber}` };
}

/** Match server gap-close: later chapters move up by one and keep their section suffixes. */
function shiftChaptersAfter(chapters: BookChapter[], removedChapterNumber: number): BookChapter[] {
    return chapters.map(chapter => {
        if (chapter.number === 0 || chapter.number <= removedChapterNumber) {
            return chapter;
        }
        const chapterNumber = chapter.number - 1;
        return {
            ...chapter,
            number: chapterNumber,
            sections: chapter.sections.map(section => withNumber(section, chapterNumber, sectionSuffix(section.number) ?? 1)),
        };
    });
}

export function removeSectionAfterTrash(chapters: BookChapter[], sectionId: string): BookChapter[] {
    const source = chapters.find(chapter => chapter.sections.some(section => section.id === sectionId));
    if (!source || source.number === 0) {
        return chapters;
    }
    const removedSuffix = sectionSuffix(source.sections.find(section => section.id === sectionId)?.number ?? '');
    const next = chapters
        .map(chapter => {
            if (chapter.id !== source.id) {
                return chapter;
            }
            return {
                ...chapter,
                sections: chapter.sections
                    .filter(section => section.id !== sectionId)
                    .map(section => {
                        const suffix = sectionSuffix(section.number);
                        if (removedSuffix == null || suffix == null || suffix <= removedSuffix) {
                            return section;
                        }
                        return withNumber(section, chapter.number, suffix - 1);
                    }),
            };
        })
        .filter(chapter => chapter.sections.length > 0);

    if (next.some(chapter => chapter.id === source.id)) {
        return next;
    }
    return shiftChaptersAfter(next, source.number);
}

export function removeChapterAfterTrash(chapters: BookChapter[], chapterId: string): BookChapter[] {
    const removed = chapters.find(chapter => chapter.id === chapterId);
    if (!removed || removed.number === 0) {
        return chapters;
    }
    return shiftChaptersAfter(
        chapters.filter(chapter => chapter.id !== chapterId),
        removed.number,
    );
}

export type MergedReorderChapter = {
    ma_zybook_chapter_id: number;
    sections: number[];
};

export function mergeChaptersByFkForReorder(chapters: BookChapter[]): MergedReorderChapter[] {
    const merged: MergedReorderChapter[] = [];
    const fkToIndex = new Map<number, number>();

    for (const chapter of chapters) {
        for (const section of chapter.sections) {
            const chapterFk = section.ma_zybook_chapter_id;
            if (!fkToIndex.has(chapterFk)) {
                fkToIndex.set(chapterFk, merged.length);
                merged.push({ ma_zybook_chapter_id: chapterFk, sections: [] });
            }
            merged[fkToIndex.get(chapterFk)!].sections.push(Number(section.id));
        }
    }

    return merged;
}

export type TocSectionPlacement = {
    chapter_number: number;
    section_number: number;
    ma_zybook_chapter_id: number;
};

export type TocReorderUpdate = {
    ma_document_id: number;
    chapter_number: number;
    section_number: number;
    ma_zybook_chapter_id: number;
};

export function placementsFromChapters(chapters: BookChapter[]): Map<number, TocSectionPlacement> {
    const placements = new Map<number, TocSectionPlacement>();

    for (const chapter of chapters) {
        for (const section of chapter.sections) {
            const sectionNumber = Number(section.number.split('.')[1]);
            placements.set(Number(section.id), {
                chapter_number: chapter.number,
                section_number: sectionNumber,
                ma_zybook_chapter_id: section.ma_zybook_chapter_id,
            });
        }
    }

    return placements;
}

function placementEqual(a: TocSectionPlacement, b: TocSectionPlacement): boolean {
    return (
        a.chapter_number === b.chapter_number && a.section_number === b.section_number && a.ma_zybook_chapter_id === b.ma_zybook_chapter_id
    );
}

export function buildTocReorderDelta(before: BookChapter[], after: BookChapter[]): TocReorderUpdate[] {
    const previous = placementsFromChapters(before);
    const next = placementsFromChapters(after);
    const updates: TocReorderUpdate[] = [];

    for (const [maDocumentId, placement] of next) {
        const old = previous.get(maDocumentId);
        if (!old || !placementEqual(old, placement)) {
            updates.push({
                ma_document_id: maDocumentId,
                ...placement,
            });
        }
    }

    updates.sort((a, b) => a.ma_document_id - b.ma_document_id);
    return updates;
}

export type MoveChapterOp = {
    type: 'move_chapter';
    from_chapter_number: number;
    to_chapter_number: number;
};

type NumberMove = { from: number; to: number };

function collectNumberMoves(mapping: Map<number, number>): NumberMove[] {
    const moves: NumberMove[] = [];
    for (const [from, to] of mapping) {
        if (from !== to) {
            moves.push({ from, to });
        }
    }
    return moves;
}

// Adjacent drags produce two reciprocal mappings (e.g. 8→9 and 9→8), but the server
// move_* ops accept one shift (e.g. from 9 to 8). Collapse that pair into the single
// op the server's range-shift SQL expects.
function collapseToSingleNumberMove(moves: NumberMove[]): NumberMove | null {
    if (moves.length !== 2) {
        return null;
    }

    const m1 = moves[0]!;
    const m2 = moves[1]!;
    if (m1.from === m2.to && m1.to === m2.from && Math.abs(m1.from - m1.to) === 1) {
        return { from: Math.max(m1.from, m2.from), to: Math.min(m1.from, m2.from) };
    }

    return null;
}

function resolveSingleNumberMove(mapping: Map<number, number>): NumberMove | null {
    const allMoves = collectNumberMoves(mapping);

    if (allMoves.length === 2) {
        const collapsed = collapseToSingleNumberMove(allMoves);
        if (collapsed !== null) {
            return collapsed;
        }
    }

    const nonAdjacentMoves = allMoves.filter(move => Math.abs(move.to - move.from) !== 1);
    if (nonAdjacentMoves.length === 1) {
        return nonAdjacentMoves[0]!;
    }

    return null;
}

function validateNumberMoveShift(mapping: Map<number, number>, move: NumberMove): boolean {
    const low = Math.min(move.from, move.to);
    const high = Math.max(move.from, move.to);
    const shift = move.from > move.to ? 1 : -1;

    for (const [from, to] of mapping) {
        if (from < low || from > high) {
            if (to !== from) {
                return false;
            }
            continue;
        }

        const expected = from === move.from ? move.to : from + shift;
        if (to !== expected) {
            return false;
        }
    }

    return true;
}

export function tryBuildMoveChapterOps(before: BookChapter[], after: BookChapter[]): MoveChapterOp[] | null {
    const prev = placementsFromChapters(before);
    const next = placementsFromChapters(after);

    if (prev.size !== next.size) {
        return null;
    }

    const oldChapterToNew = new Map<number, number>();

    for (const [maDocumentId, newPlacement] of next) {
        const oldPlacement = prev.get(maDocumentId);
        if (!oldPlacement) {
            return null;
        }

        if (
            oldPlacement.section_number !== newPlacement.section_number ||
            oldPlacement.ma_zybook_chapter_id !== newPlacement.ma_zybook_chapter_id
        ) {
            return null;
        }

        if (oldPlacement.chapter_number === newPlacement.chapter_number) {
            continue;
        }

        const mapped = oldChapterToNew.get(oldPlacement.chapter_number);
        if (mapped !== undefined && mapped !== newPlacement.chapter_number) {
            return null;
        }
        oldChapterToNew.set(oldPlacement.chapter_number, newPlacement.chapter_number);
    }

    if (oldChapterToNew.size === 0) {
        return [];
    }

    const resolved = resolveSingleNumberMove(oldChapterToNew);
    if (resolved === null || !validateNumberMoveShift(oldChapterToNew, resolved)) {
        return null;
    }

    return [
        {
            type: 'move_chapter',
            from_chapter_number: resolved.from,
            to_chapter_number: resolved.to,
        },
    ];
}

export type MoveSectionOp = {
    type: 'move_section';
    ma_document_id: number;
    chapter_number: number;
    from_section_number: number;
    to_section_number: number;
};

export type TocReorderOp = MoveChapterOp | MoveSectionOp;

export function tryBuildMoveSectionOps(before: BookChapter[], after: BookChapter[]): MoveSectionOp[] | null {
    const prev = placementsFromChapters(before);
    const next = placementsFromChapters(after);

    if (prev.size !== next.size) {
        return null;
    }

    const oldSectionToNew = new Map<number, number>();
    let chapterNumber: number | null = null;

    for (const [maDocumentId, newPlacement] of next) {
        const oldPlacement = prev.get(maDocumentId);
        if (!oldPlacement) {
            return null;
        }

        if (placementEqual(oldPlacement, newPlacement)) {
            continue;
        }

        if (
            oldPlacement.chapter_number !== newPlacement.chapter_number ||
            oldPlacement.ma_zybook_chapter_id !== newPlacement.ma_zybook_chapter_id
        ) {
            return null;
        }

        if (chapterNumber === null) {
            chapterNumber = oldPlacement.chapter_number;
        } else if (chapterNumber !== oldPlacement.chapter_number) {
            return null;
        }

        const mapped = oldSectionToNew.get(oldPlacement.section_number);
        if (mapped !== undefined && mapped !== newPlacement.section_number) {
            return null;
        }
        oldSectionToNew.set(oldPlacement.section_number, newPlacement.section_number);
    }

    if (oldSectionToNew.size === 0) {
        return [];
    }

    const moves: Array<NumberMove & { maDocumentId: number }> = [];
    for (const [from, to] of oldSectionToNew) {
        if (from === to) {
            continue;
        }

        let maDocumentId: number | null = null;
        for (const [id, placement] of prev) {
            if (placement.chapter_number === chapterNumber && placement.section_number === from) {
                maDocumentId = id;
                break;
            }
        }
        if (maDocumentId === null) {
            return null;
        }

        moves.push({ from, to, maDocumentId });
    }

    const resolved = resolveSingleNumberMove(oldSectionToNew);
    if (resolved === null || !validateNumberMoveShift(oldSectionToNew, resolved)) {
        return null;
    }

    const maDocumentId = moves.find(move => move.from === resolved.from)?.maDocumentId;
    if (maDocumentId === undefined) {
        return null;
    }

    return [
        {
            type: 'move_section',
            ma_document_id: maDocumentId,
            chapter_number: chapterNumber!,
            from_section_number: resolved.from,
            to_section_number: resolved.to,
        },
    ];
}

export function chapterBlockIdsUnchanged(a: BookChapter[], b: BookChapter[]): boolean {
    if (a.length !== b.length) {
        return false;
    }
    return a.every((chapter, index) => chapter.id === b[index]?.id);
}

export function chapterTreesEqual(a: BookChapter[], b: BookChapter[]): boolean {
    if (a.length !== b.length) {
        return false;
    }

    return a.every((chapter, chapterIndex) => {
        const other = b[chapterIndex];
        if (!other || chapter.id !== other.id) {
            return false;
        }

        if (chapter.sections.length !== other.sections.length) {
            return false;
        }

        return chapter.sections.every((section, sectionIndex) => section.id === other.sections[sectionIndex]?.id);
    });
}

export function remapExpandedChapterIds(oldChapters: BookChapter[], newChapters: BookChapter[], expandedChapterIds: string[]): string[] {
    const expandedAnchorSectionIds = expandedChapterIds
        .map(id => oldChapters.find(chapter => chapter.id === id)?.sections[0]?.id)
        .filter((id): id is string => id != null);

    return newChapters
        .filter(chapter => chapter.sections[0] != null && expandedAnchorSectionIds.includes(chapter.sections[0].id))
        .map(chapter => chapter.id);
}
